import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./StudentResultSpreadsheet.css";
import { RootState } from "../../../../Redux/store";
import { getValue } from "../../../../utils/object.utils";
import { Colors } from "../../../../Constants/colors";
import { FontFamily } from "../../../../Constants/Enums/FontFamily.enums";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FLEX_DIRECTION,
  JUSTIFY_CONTENT,
  SPACING,
} from "../../../../Constants/Enums/styles.enum";
import { BtnSize, BtnType, Button } from "../../../Button";
import { Pagination } from "../../../pagination";
import {
  clearDirtyStudentIds,
  updatePageNo,
} from "../../../../Redux/Slices/studentResuilt.slice";
import {
  useDownloadStudentResult,
  useSaveAllStudentResults,
} from "../../../../hooks/resultsHooks/studentResult.hooks";
import { useDebouncedStudentResultAutosave } from "../../../../hooks/resultsHooks/useDebouncedStudentResultAutosave";
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../../../Redux/Slices/alert.slice";
import { parseApiError } from "../../../../Helper/apiError.helper";
import { StudentResultTitle } from "../../../../Constants/tableTitles/TableFormTitle/studentResult.tableForm.title";
import { buildBulkStudentResultPayload } from "../../../../Services/results/results";
import {
  getSubjectColumns,
  getSubjectColumnTitle,
  isGradeOnlySubject,
} from "./subjectColumns";
import { StudentResultRow } from "./StudentResultRow";

interface Props {
  resultId: number;
}

const gradeColumnConfig: any = StudentResultTitle.find(
  (column) => column.field === "grade",
);
const GRADE_OPTIONS: string[] = gradeColumnConfig?.valueOptions || [
  "A+",
  "A",
  "B+",
  "B",
  "C+",
  "C",
  "D+",
  "D",
  "E",
];

export const StudentResultSpreadsheet = ({ resultId }: Props) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [downloadingStudentId, setDownloadingStudentId] = useState<
    number | null
  >(null);

  const { paginatedList, dirtyStudentIds } = useSelector(
    (state: RootState) => ({
      paginatedList: state.studentResult.paginatedStudentResultList,
      dirtyStudentIds: state.studentResult.dirtyStudentIds,
    }),
  );

  const studentList: any[] = getValue(paginatedList, "result", []);
  const totalPage = getValue(paginatedList, "lastPage", 0);

  const filteredStudentList = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return studentList;

    return studentList.filter((student) => {
      const fullName = getValue(student, "full_name", "").toLowerCase();
      const firstName = getValue(student, "first_name", "").toLowerCase();
      const lastName = getValue(student, "last_name", "").toLowerCase();

      return (
        fullName.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query)
      );
    });
  }, [studentList, searchQuery]);

  const subjectColumns = useMemo(
    () => getSubjectColumns(studentList),
    [studentList],
  );

  const { isLoading: isSavingAll, mutate: saveAllStudentResults } =
    useSaveAllStudentResults({
      onSuccess: () => {
        dispatch(clearDirtyStudentIds());
        dispatch(setSuccessMessage("Results saved successfully"));
      },
      onError: (error: Error) => {
        dispatch(setErrorMessage(parseApiError(error)));
      },
    });

  const { cancelPendingAutosaves } = useDebouncedStudentResultAutosave({
    resultId,
    paused: isSavingAll,
  });

  const { mutate: downloadStudentResultReport } = useDownloadStudentResult({
    onSuccess: (studentResult) => {
      setDownloadingStudentId(null);
      if (studentResult) {
        window.open(studentResult, "_blank");
      }
    },
    onError: (error: Error) => {
      setDownloadingStudentId(null);
      dispatch(setErrorMessage(parseApiError(error)));
    },
  });

  const handleExport = (student: any) => {
    const link = getValue(student, "link", null);
    const studentId = getValue(student, "student_id");

    if (link) {
      window.open(link, "_blank");
      return;
    }

    setDownloadingStudentId(studentId);
    downloadStudentResultReport({ studentId, resultId });
  };

  const handleSaveAll = () => {
    const payload = buildBulkStudentResultPayload({
      resultId,
      students: studentList,
    });

    if (!payload.students.length) {
      dispatch(setErrorMessage("No students to save"));
      return;
    }

    cancelPendingAutosaves();
    saveAllStudentResults(payload);
  };

  const dirtyCount = dirtyStudentIds.length;

  return (
    <div
      style={{
        display: DISPLAY.FLEX,
        flexDirection: FLEX_DIRECTION.COLUMN,
        gap: SPACING.S16,
        width: "100%",
        minWidth: 0,
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: DISPLAY.FLEX,
          flexDirection: FLEX_DIRECTION.ROW,
          justifyContent: JUSTIFY_CONTENT.SPACE_BETWEEN,
          alignItems: ALIGN_ITEMS.CENTER,
          gap: SPACING.S12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", minWidth: "260px", flex: "1 1 260px" }}>
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search student by name..."
            style={{
              width: "100%",
              height: "42px",
              border: `1.5px solid ${Colors.LightGrey}`,
              borderRadius: "8px",
              padding: "0 14px",
              fontSize: "13px",
              fontFamily: FontFamily.ROBOTO_REGULAR,
              color: Colors.Black,
              boxSizing: "border-box",
              outline: "none",
            }}
          />
        </div>

        <div
          style={{
            display: DISPLAY.FLEX,
            alignItems: ALIGN_ITEMS.CENTER,
            gap: SPACING.S12,
          }}
        >
          {dirtyCount > 0 && !isSavingAll && (
            <span
              style={{
                fontSize: "12px",
                fontFamily: FontFamily.ROBOTO_REGULAR,
                color: Colors.TangerineOrange,
              }}
            >
              {dirtyCount} unsaved change{dirtyCount !== 1 ? "s" : ""}
            </span>
          )}
          <Button
            text="Save all"
            btnType={BtnType.primary}
            btnSize={BtnSize.md}
            loading={isSavingAll}
            onClick={handleSaveAll}
          />
        </div>
      </div>

      {/* Spreadsheet */}
      {filteredStudentList.length === 0 ? (
        <div className="rs-empty-state">
          {searchQuery
            ? "No students match your search."
            : "No students found for this result."}
        </div>
      ) : (
        <div className="rs-spreadsheet-wrapper">
          <table className="rs-spreadsheet-table">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className="rs-sticky-corner rs-plain-header"
                  style={{ textAlign: "left" }}
                >
                  Student
                </th>
                {subjectColumns.map((column) => {
                  const gradeOnly = isGradeOnlySubject(column.subject_type);
                  return (
                    <th
                      key={column.subject_id}
                      colSpan={gradeOnly ? 1 : 2}
                      className="rs-sticky-header rs-subject-group-header"
                    >
                      {getSubjectColumnTitle(column)}
                      {!gradeOnly && (
                        <span className="rs-full-marks-hint">
                          Full marks: {column.full_marks}
                        </span>
                      )}
                    </th>
                  );
                })}
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  %
                </th>
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  Position
                </th>
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  Attendance
                </th>
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  Height (cm)
                </th>
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  Weight (kg)
                </th>
                <th rowSpan={2} className="rs-sticky-header rs-plain-header">
                  Remarks
                </th>
                <th
                  rowSpan={2}
                  className="rs-sticky-header rs-sticky-corner-right rs-plain-header"
                >
                  Report
                </th>
              </tr>
              <tr>
                {subjectColumns.map((column) => (
                  <React.Fragment key={column.subject_id}>
                    {!isGradeOnlySubject(column.subject_type) && (
                      <th className="rs-sticky-header rs-subheader">Marks</th>
                    )}
                    <th className="rs-sticky-header rs-subheader">Grade</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudentList.map((student, rowIndex) => {
                const studentId = getValue(student, "student_id");
                return (
                  <StudentResultRow
                    key={studentId}
                    student={student}
                    subjectColumns={subjectColumns}
                    gradeOptions={GRADE_OPTIONS}
                    isDirty={dirtyStudentIds.includes(studentId)}
                    isDownloading={downloadingStudentId === studentId}
                    rowIndex={rowIndex}
                    onExport={handleExport}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        totalPage={totalPage}
        onPageChange={(pageNo) => {
          dispatch(updatePageNo(pageNo));
        }}
      />
    </div>
  );
};
